import React, { useState, useEffect } from "react";
import axios from "axios";
import { Form, Input, Button, Upload, message, Card, Spin, Row, Col } from "antd";
import { UploadOutlined, FilePdfOutlined, IdcardOutlined, BookOutlined } from "@ant-design/icons";
import "./setting.css"; // Reuse styling variables if needed

const PublicOnboardingForm = () => {
  const [form] = Form.useForm();
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // File states
  const [resumeFile, setResumeFile] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [degreeFile, setDegreeFile] = useState(null);
  const [doc1File, setDoc1File] = useState(null);
  const [doc2File, setDoc2File] = useState(null);

  const queryParams = new URLSearchParams(window.location.search);
  const companyId = queryParams.get("companyId");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:9003/api";

  useEffect(() => {
    if (!companyId) {
      message.error("Invalid onboarding link: Company ID is missing");
      setLoading(false);
      return;
    }

    axios
      .get(`${API_URL}/common/public-company/${companyId}`)
      .then((res) => {
        if (res.data && res.data.status === 1) {
          setCompanyName(res.data.data.name);
        } else {
          message.error("Failed to load company details");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Error connecting to onboarding service");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [companyId, API_URL]);

  const beforeUploadPdfOnly = (file) => {
    const isPdf = file.type === "application/pdf";
    if (!isPdf) {
      message.error("You can only upload PDF files!");
    }
    return isPdf || Upload.LIST_IGNORE;
  };

  const beforeUploadJpgOrPdf = (file) => {
    const isValidType =
      file.type === "application/pdf" ||
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";
    if (!isValidType) {
      message.error("You can only upload PDF or JPG/PNG files!");
    }
    return isValidType || Upload.LIST_IGNORE;
  };

  const handleFinish = async (values) => {
    if (!resumeFile) {
      message.error("Please upload your resume");
      return;
    }
    if (!aadharFile) {
      message.error("Please upload your Aadhar card");
      return;
    }
    if (!degreeFile) {
      message.error("Please upload your degree certificate");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("companyId", companyId);
    formData.append("name", values.name);
    formData.append("email", values.email);
    formData.append("mobileNo", values.mobileNo);
    formData.append("currentAddress", values.currentAddress);
    formData.append("permanentAddress", values.permanentAddress);
    formData.append("previousCompany", values.previousCompany || "");
    formData.append("previousSalary", values.previousSalary || "");
    formData.append("qualification", values.qualification || "");

    formData.append("resume", resumeFile);
    formData.append("aadharCard", aadharFile);
    formData.append("degreeCertificate", degreeFile);
    if (doc1File) formData.append("document1", doc1File);
    if (doc2File) formData.append("document2", doc2File);

    try {
      const response = await axios.post(`${API_URL}/common/onboarding/submit`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data && response.data.status === 1) {
        message.success("Onboarding form submitted successfully!");
        setSubmitted(true);
      } else {
        message.error(response.data.message || "Failed to submit form");
      }
    } catch (error) {
      console.error(error);
      message.error(error.response?.data?.message || "Server error while submitting application");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading onboarding details..." />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", padding: 20 }}>
        <Card style={{ maxWidth: 500, textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          <h2 style={{ color: "#d9534f" }}>Invalid Link</h2>
          <p>This onboarding link is invalid or expired. Please contact your recruiter or HR manager.</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", padding: 20, background: "#f5f7fa" }}>
        <Card style={{ maxWidth: 600, textAlign: "center", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", border: "none" }}>
          <div style={{ fontSize: 60, color: "#52c41a", marginBottom: 20 }}>✓</div>
          <h2 style={{ fontFamily: "Outfit, Inter, sans-serif", fontWeight: 600, color: "#1a3353" }}>Submission Successful</h2>
          <p style={{ color: "#666", fontSize: "16px", marginTop: 10 }}>
            Thank you, <strong>{form.getFieldValue("name")}</strong>! Your onboarding details and documentation have been submitted to <strong>{companyName}</strong>.
          </p>
          <p style={{ color: "#888", fontSize: "14px", marginTop: 15 }}>
            The HR team will review your application. Once approved, you will receive an email with activation details and login credentials to access the platform.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", minHeight: "100vh", padding: "40px 20px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <Card
          style={{
            borderRadius: 16,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
            border: "none",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: "40px 30px" }}
        >
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h1 style={{ fontFamily: "Outfit, Inter, sans-serif", fontSize: 28, fontWeight: 700, color: "#13487D", margin: 0 }}>
              Onboarding Form
            </h1>
            <p style={{ color: "#666", marginTop: 8, fontSize: 16 }}>
              Welcome to the team! Please complete the form below to initiate your onboarding with <strong>{companyName}</strong>.
            </p>
          </div>

          <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark="optional">
            <h3 style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 8, marginBottom: 20, color: "#13487D" }}>
              Personal Details
            </h3>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Full Name"
                  name="name"
                  rules={[{ required: true, message: "Please enter your full name" }]}
                >
                  <Input placeholder="John Doe" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email address" },
                  ]}
                >
                  <Input placeholder="johndoe@example.com" size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Mobile Number"
                  name="mobileNo"
                  rules={[{ required: true, message: "Please enter your mobile number" }]}
                >
                  <Input placeholder="e.g. +91 9876543210" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Highest Qualification"
                  name="qualification"
                  rules={[{ required: true, message: "Please enter your highest qualification" }]}
                >
                  <Input placeholder="e.g. B.Tech in Computer Science" size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Previous Company (if applicable)" name="previousCompany">
                  <Input placeholder="Previous Employer Name" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Previous Salary (LPA / Monthly)" name="previousSalary">
                  <Input placeholder="e.g. 6,00,000" size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Current Address"
              name="currentAddress"
              rules={[{ required: true, message: "Please enter your current address" }]}
            >
              <Input.TextArea placeholder="Enter your full current residential address" rows={3} />
            </Form.Item>

            <Form.Item
              label="Permanent Address"
              name="permanentAddress"
              rules={[{ required: true, message: "Please enter your permanent address" }]}
            >
              <Input.TextArea placeholder="Enter your permanent address as per government documents" rows={3} />
            </Form.Item>

            <h3 style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 8, marginTop: 30, marginBottom: 20, color: "#13487D" }}>
              Upload Documents
            </h3>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Resume / CV"
                  extra="Required. PDF format only."
                  required
                >
                  <Upload
                    maxCount={1}
                    beforeUpload={beforeUploadPdfOnly}
                    onRemove={() => setResumeFile(null)}
                    customRequest={({ file, onSuccess }) => {
                      setResumeFile(file);
                      onSuccess("ok");
                    }}
                  >
                    <Button icon={<FilePdfOutlined />} style={{ width: "100%" }}>
                      Upload Resume
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  label="Aadhar Card"
                  extra="Required. JPG/PNG/PDF formats."
                  required
                >
                  <Upload
                    maxCount={1}
                    beforeUpload={beforeUploadJpgOrPdf}
                    onRemove={() => setAadharFile(null)}
                    customRequest={({ file, onSuccess }) => {
                      setAadharFile(file);
                      onSuccess("ok");
                    }}
                  >
                    <Button icon={<IdcardOutlined />} style={{ width: "100%" }}>
                      Upload Aadhar
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  label="Degree Certificate"
                  extra="Required. JPG/PNG/PDF formats."
                  required
                >
                  <Upload
                    maxCount={1}
                    beforeUpload={beforeUploadJpgOrPdf}
                    onRemove={() => setDegreeFile(null)}
                    customRequest={({ file, onSuccess }) => {
                      setDegreeFile(file);
                      onSuccess("ok");
                    }}
                  >
                    <Button icon={<BookOutlined />} style={{ width: "100%" }}>
                      Upload Certificate
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 10 }}>
              <Col xs={24} md={12}>
                <Form.Item label="Extra Document 1" extra="Optional. Any additional file.">
                  <Upload
                    maxCount={1}
                    beforeUpload={beforeUploadJpgOrPdf}
                    onRemove={() => setDoc1File(null)}
                    customRequest={({ file, onSuccess }) => {
                      setDoc1File(file);
                      onSuccess("ok");
                    }}
                  >
                    <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                      Upload Extra Doc 1
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Extra Document 2" extra="Optional. Any additional file.">
                  <Upload
                    maxCount={1}
                    beforeUpload={beforeUploadJpgOrPdf}
                    onRemove={() => setDoc2File(null)}
                    customRequest={({ file, onSuccess }) => {
                      setDoc2File(file);
                      onSuccess("ok");
                    }}
                  >
                    <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                      Upload Extra Doc 2
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ marginTop: 40, textAlign: "center" }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                style={{
                  padding: "0 60px",
                  borderRadius: 8,
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  backgroundColor: "#13487D",
                  borderColor: "#13487D",
                }}
              >
                Submit Application
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default PublicOnboardingForm;
